import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiChevronUp, FiHelpCircle, FiMessageCircle, FiMail, FiArrowRight } from 'react-icons/fi';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const faqs = [
    {
      question: "How is this different from other lead gen agencies?",
      answer: "Unlike typical agencies that just send bulk emails, we focus on a systematic, qualification-first pipeline. We combine outbound, demand gen, and authority positioning to ensure you only talk to high-intent buyers.",
      category: "Process"
    },
    {
      question: "How quickly will I see results?",
      answer: "Our system is live within 2 - 3 weeks. Most clients see their first qualified meetings in weeks 4 - 6. By month 3, you should be seeing a steady flow of 15 - 20 qualified conversations per month.",
      category: "Timeline"
    },
    {
      question: "What's the minimum commitment?",
      answer: "We typically work on a 3-month initial foundation phase to ensure the engines are fully optimized before scaling, although we offer flexible structures based on your specific needs.",
      category: "Contract"
    },
    {
      question: "What industries do you work with?",
      answer: "We specialize in B2B service-based companies, particularly in SaaS, Professional Services, and Enterprise Tech where the sales cycle requires high-trust positioning.",
      category: "Expertise"
    },
    {
      question: "Do I need to create content myself?",
      answer: "No, our 'Authority Positioning' engine handles the heavy lifting. We extract your expertise through brief syncs and turn it into high-performing content for your social channels.",
      category: "Content"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={sectionRef} className="bg-black text-white py-28 px-6 font-sans relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-80 h-80 bg-emerald-500/5 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px] animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[130px]"></div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(14,214,183,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(14,214,183,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-emerald-500/20 rounded-full animate-pulse"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">

        {/* Header with Animation */}
        <div className={`text-center mb-16 transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-500/30 mb-6">
            <FiHelpCircle className="text-emerald-400 w-4 h-4 animate-pulse" />
            <span className="text-emerald-400 text-xs font-bold tracking-[2px] uppercase">FAQ</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
            Common <span className="relative inline-block">
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Questions
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 8" fill="none">
                <path d="M0 4 L200 4" stroke="url(#gradientFaq)" strokeWidth="2" strokeDasharray="5 5" />
                <defs>
                  <linearGradient id="gradientFaq" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Everything you need to know about how we design, develop, and deliver high-quality software solutions
          </p>
        </div>

        {/* Stats Row */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 transition-all duration-700 delay-200 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>

          <div className="bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-5 text-center hover:border-emerald-500/30 transition-all duration-300 hover:scale-105 group">
            <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2">2-6</div>
            <p className="text-gray-400 text-sm">Weeks Project Delivery</p>
            <div className="w-0 group-hover:w-full h-px bg-gradient-to-r from-emerald-500 to-transparent mx-auto transition-all duration-500 mt-2"></div>
          </div>

          <div className="bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-5 text-center hover:border-emerald-500/30 transition-all duration-300 hover:scale-105 group">
            <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2">50+</div>
            <p className="text-gray-400 text-sm">Projects Successfully Delivered</p>
            <div className="w-0 group-hover:w-full h-px bg-gradient-to-r from-emerald-500 to-transparent mx-auto transition-all duration-500 mt-2"></div>
          </div>

          <div className="bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-5 text-center hover:border-emerald-500/30 transition-all duration-300 hover:scale-105 group">
            <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2">24/7</div>
            <p className="text-gray-400 text-sm">Support & Maintenance</p>
            <div className="w-0 group-hover:w-full h-px bg-gradient-to-r from-emerald-500 to-transparent mx-auto transition-all duration-500 mt-2"></div>
          </div>

        </div>

        {/* Accordion List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`group transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              <div
                className={`border rounded-2xl transition-all duration-300 ${openIndex === index
                  ? 'border-emerald-500/30 bg-gradient-to-br from-gray-900 to-gray-950 shadow-2xl shadow-emerald-500/5'
                  : 'border-gray-800 bg-gray-900/30 hover:border-gray-700'
                  }`}
              >
                {/* Question Header */}
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none group/btn"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Category Badge */}
                    <div className={`hidden sm:block px-3 py-1 rounded-full text-[10px] font-bold tracking-wider ${openIndex === index
                      ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-gray-800 text-gray-500 border border-gray-700'
                      }`}>
                      {faq.category}
                    </div>
                    <span className={`text-base md:text-lg font-semibold tracking-tight transition-all duration-300 ${openIndex === index
                      ? 'text-white bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent'
                      : 'text-gray-300 group-hover/btn:text-white'
                      }`}>
                      {faq.question}
                    </span>
                  </div>
                  <div className={`ml-4 p-2 rounded-lg transition-all duration-300 ${openIndex === index
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-gray-800/50 text-gray-500 group-hover/btn:bg-gray-700'
                    }`}>
                    {openIndex === index ? (
                      <FiChevronUp className="w-5 h-5" />
                    ) : (
                      <FiChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </button>

                {/* Answer Content - Smooth Opening */}
                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="px-6 pb-6 pt-2 border-t border-gray-800/50">
                    <div className="flex gap-3">
                      <div className="w-1 bg-gradient-to-b from-emerald-500 to-cyan-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                          {faq.answer}
                        </p>

                        {/* Additional CTA for open state */}
                        {openIndex === index && (
                          <button className="mt-4 inline-flex items-center gap-2 text-emerald-400 text-sm font-medium hover:gap-3 transition-all duration-300 group/cta">
                            <span>Learn more about this</span>
                            <FiArrowRight className="w-3 h-3 group-hover/cta:translate-x-1 transition-transform" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions Section */}
        <div className={`mt-16 transition-all duration-700 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-50 blur transition duration-500"></div>
            <div className="relative bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-8 group-hover:border-emerald-500/30 transition-all duration-500">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/30">
                    <FiMessageCircle className="text-emerald-400 w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-1">Still have questions?</h3>
                    <p className="text-gray-400 text-sm">We're here to help you understand the process</p>
                  </div>
                </div>
                <button className="group/btn inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full font-semibold text-black hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 hover:scale-105">
                  <FiMail className="w-4 h-4" />
                  <span>Contact Our Team</span>
                  <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;